import { observer } from 'mobx-react-lite'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { Alert, Button, DatePicker, Input, Segmented, Upload, notification } from 'antd'
import {
    AndroidFilled,
    AppleFilled,
    ClockCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    FilterOutlined,
    GiftOutlined,
    InboxOutlined,
    LinkOutlined,
    MobileOutlined,
    SendOutlined,
    ShoppingCartOutlined,
    TeamOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import utc from 'dayjs/plugin/utc'
import pushesController from './pushes.controller'
import { ParamTypes } from '../../../helpers/types.interfaces'
import __select from '../../../helpers/select_entity/__select.controller'
import { SelectEntity } from '../../../helpers/select_entity/SelectEntity'
import { SelectUsersModal } from '../../../components/users/SelectUsersModal'
import { SectionBadge, PlainField } from '../../../components/SectionCard'
import { ErrorByPricingPlan } from '../../../components/ErrorByPricingPlan'
import { storeLocal } from '../../../helpers/stores'
import { returnParseProjectElement } from '../../../helpers/project.controller'
import __users, { PUSH_SEGMENT_PLAN } from '../../users/users.controller'
import { ListChip, ListMetric } from '../../../components/card_list/CardList'
import { fillBonusVars, PushBonusFields } from './PushBonus'
import cartsController from '../carts/carts.controller'
import { cartsFilterChips } from '../carts/carts.helpers'
import './pushes.less'

dayjs.extend(utc)

const TITLE_MAX = 50
const TEXT_MAX = 150
const IMAGE_MAX_BYTES = 500000

type PreviewPlatform = 'ios' | 'android'

const Section = ({ icon, title, background, children }: {
    icon: React.ReactNode
    title: React.ReactNode
    background: string
    children: React.ReactNode
}) => (
    <div className="order__block" style={{ width: '100%', height: 'auto' }}>
        <SectionBadge icon={icon} text={title} background={background} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
            {children}
        </div>
    </div>
)

const locale = () => {
    const lang = storeLocal.get('lang') || process.env.REACT_APP_DEFAULT_LANGUAGE || 'en'
    return lang === 'ua' ? 'uk' : lang
}

// Превью рисуем по текущим полям формы: картинка — новая (File) или уже загруженная
const useImageUrl = (): string | null => {
    const file: any = pushesController.push_create.image
    const old = pushesController.oldImage

    const fileUrl = useMemo(() => file instanceof Blob ? URL.createObjectURL(file) : null, [file])

    useEffect(() => () => {
        if (fileUrl) URL.revokeObjectURL(fileUrl)
    }, [fileUrl])

    if (fileUrl) return fileUrl
    if (old && old.uri) return `${old.base ?? ''}${old.uri}`
    return null
}

const NotificationPreview = observer(({ platform, appName, image }: { platform: PreviewPlatform, appName: string, image: string | null }) => {
    const title = fillBonusVars(pushesController.push_create.title, pushesController.push_create).trim()
    const text = fillBonusVars(pushesController.push_create.description ?? '', pushesController.push_create).trim()
    const now = new Date()
    const time = dayjs().format('HH:mm')
    const date = new Intl.DateTimeFormat(locale(), { weekday: 'long', day: 'numeric', month: 'long' }).format(now)
    const initial = (appName || 'S').trim().charAt(0).toUpperCase()
    const android = platform === 'android'

    return (
        <div className={`pu-phone${android ? ' pu-phone--android' : ''}`}>
            <div className="pu-phone__screen">
                <div className="pu-phone__island" />
                <div className="pu-phone__status">
                    <span>{time}</span>
                    <span>5G ▮▮▮</span>
                </div>
                <div className="pu-phone__clock">
                    <div className="pu-phone__clock-date">{date}</div>
                    <div className="pu-phone__clock-time">{time}</div>
                </div>

                <div className="pu-phone__stack">
                    <div key={platform} className={`pu-notif${android ? ' pu-notif--android' : ''}`}>
                        <div className="pu-notif__row">
                            {!android && <span className="pu-notif__icon">{initial}</span>}
                            <div className="pu-notif__content">
                                <div className="pu-notif__meta">
                                    {android && <span className="pu-notif__icon" style={{ marginRight: 6 }}>{initial}</span>}
                                    <span className="pu-notif__app">{appName}</span>
                                    <span>{android ? `· ${i.t('pushes:editor.now')}` : i.t('pushes:editor.now')}</span>
                                </div>
                                <div className={`pu-notif__title${title ? '' : ' pu-notif__placeholder'}`}>
                                    {title || i.t('pushes:editor.sampleTitle')}
                                </div>
                                <div className={`pu-notif__text${text ? '' : ' pu-notif__placeholder'}`}>
                                    {text || i.t('pushes:editor.sampleText')}
                                </div>
                            </div>
                            {!android && image && <span className="pu-notif__thumb"><img src={image} alt="" /></span>}
                        </div>
                        {android && image && <div className="pu-notif__image"><img src={image} alt="" /></div>}
                    </div>
                </div>
            </div>
        </div>
    )
})

const ImageField = observer(({ image }: { image: string | null }) => {
    const hasNew = (pushesController.push_create.image as any) instanceof Blob
    const hasOld = pushesController.oldImage && pushesController.oldImage.id

    return (
        <div className="pu-upload">
            {image &&
                <div className="pu-upload__preview">
                    <img src={image} alt="" />
                </div>
            }
            <Upload.Dragger
                accept="image/png"
                maxCount={1}
                showUploadList={false}
                beforeUpload={(file) => {
                    if (file.size > IMAGE_MAX_BYTES) {
                        notification.error({
                            message: i.t('def:errors.fileSizeTitle'),
                            description: i.t('def:errors.fileSizeDescription')
                        })
                        return Upload.LIST_IGNORE
                    }
                    if (file.type !== 'image/png') {
                        notification.error({
                            message: i.t('def:errors.fileTypeTitle'),
                            description: i.t('def:errors.fileTypeDescription')
                        })
                        return Upload.LIST_IGNORE
                    }
                    pushesController.upload(file)
                    return false
                }}
            >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">{i.t('pushes:editor.imageDrop')}</p>
                <p className="ant-upload-hint">{i.t('pushes:editor.imageHint')}</p>
            </Upload.Dragger>
            {(hasNew || hasOld) &&
                <Button
                    danger
                    shape="circle"
                    icon={<DeleteOutlined />}
                    onClick={() => hasNew
                        ? pushesController.push_create.image = null
                        : pushesController.deletePicture(pushesController.oldImage.id)
                    }
                />
            }
        </div>
    )
})

// «1–2», «от 3», «до 5», «1»
const rangeText = (from: any, to: any) => {
    const has = (value: any) => value !== null && value !== undefined && value !== ''
    if (has(from) && has(to)) return `${from}` === `${to}` ? `${from}` : `${from}–${to}`
    if (has(from)) return i.t('pushes:segment.rangeFrom', { value: from })
    return i.t('pushes:segment.rangeTo', { value: to })
}

// Аудитория рассылки по сегменту из списка клиентов: условия, размер и сколько получат пуш
const SegmentAudience = observer(() => {
    const segment = pushesController.segment
    const size = pushesController.segmentSize
    const locked = storeLocal.get('pricing_plan') < PUSH_SEGMENT_PLAN
    const has = (key: string) => segment[key] !== null && segment[key] !== undefined && segment[key] !== ''

    const products: string[] = (segment.products ?? []).map((id: number) => segment.product_labels?.[id] ?? `#${id}`)
    const warehouses: string[] = (segment.warehouses ?? []).map((id: number) => segment.warehouse_labels?.[id] ?? `#${id}`)
    const priceTypes: string[] = (segment.price_types ?? []).map((id: number) => segment.price_type_labels?.[id] ?? `#${id}`)

    return (
        <div className="pu-segment">
            <div className="pu-segment__head">
                <span className="pu-segment__title"><FilterOutlined /> {i.t('pushes:segment.title')}</span>
                <span className="pu-segment__links">
                    <Button type="link" size="small" href={__users.filtersUrl(segment)}>{i.t('pushes:segment.edit')}</Button>
                    <Button type="link" size="small" onClick={() => pushesController.clearSegment()}>{i.t('pushes:segment.manual')}</Button>
                </span>
            </div>

            <div className="pu-segment__chips">
                {(has('orders_from') || has('orders_to')) &&
                    <ListChip tone="primary">{i.t('pushes:segment.orders', { range: rangeText(segment.orders_from, segment.orders_to) })}</ListChip>
                }
                {(has('days_from') || has('days_to')) &&
                    <ListChip tone="primary">{i.t('pushes:segment.days', { range: rangeText(segment.days_from, segment.days_to) })}</ListChip>
                }
                {products.length > 0 &&
                    <ListChip tone="primary" tooltip={products.join(', ')}>{i.t('pushes:segment.products', { list: products.join(', ') })}</ListChip>
                }
                {warehouses.length > 0 &&
                    <ListChip tone="primary" tooltip={warehouses.join(', ')}>{i.t('pushes:segment.warehouses', { list: warehouses.join(', ') })}</ListChip>
                }
                {priceTypes.length > 0 &&
                    <ListChip tone="primary" tooltip={priceTypes.join(', ')}>{i.t('pushes:segment.priceTypes', { list: priceTypes.join(', ') })}</ListChip>
                }
                {has('name') && <ListChip>{i.t('pushes:segment.name', { value: segment.name })}</ListChip>}
                {has('phone') && <ListChip>{i.t('pushes:segment.phone', { value: segment.phone })}</ListChip>}
                {has('email') && <ListChip>{i.t('pushes:segment.email', { value: segment.email })}</ListChip>}
            </div>

            <div className="pu-segment__stats">
                <ListMetric value={size ? size.count.toLocaleString() : null} label={i.t('pushes:segment.count')} />
                <ListMetric
                    value={size ? size.reachable.toLocaleString() : null}
                    label={i.t('pushes:segment.reachable')}
                    hint={i.t('pushes:segment.reachableHint')}
                    tone={size && size.reachable > 0 ? 'success' : undefined}
                />
            </div>

            {size && size.count === 0 && <Alert type="error" showIcon message={i.t('pushes:segment.empty')} />}
            {locked &&
                <Alert
                    type="warning"
                    showIcon
                    message={i.t('pushes:segment.planHint')}
                    action={<Button size="small" href="/billing">{i.t('pushes:segment.plans')}</Button>}
                />
            }
            <div className="pu-hint">{i.t('pushes:segment.fixedHint')}</div>
        </div>
    )
})

// Аудитория рассылки владельцам корзин из списка корзин: условия, размер и сколько получат пуш
const CartsAudience = observer(() => {
    const history = useHistory()
    const segment = pushesController.cartsSegment!
    const size = pushesController.segmentSize
    const locked = storeLocal.get('pricing_plan') < PUSH_SEGMENT_PLAN
    const chips = cartsFilterChips(segment)

    const editConditions = () => {
        cartsController.applySegment(segment)
        history.push('/carts')
    }

    return (
        <div className="pu-segment">
            <div className="pu-segment__head">
                <span className="pu-segment__title"><ShoppingCartOutlined /> {i.t('pushes:segment.cartsTitle')}</span>
                <span className="pu-segment__links">
                    <Button type="link" size="small" onClick={editConditions}>{i.t('pushes:segment.edit')}</Button>
                    <Button type="link" size="small" onClick={() => pushesController.clearSegment()}>{i.t('pushes:segment.manual')}</Button>
                </span>
            </div>

            <div className="pu-segment__chips">
                {chips.length > 0
                    ? chips.map(chip => <ListChip key={chip} tone="primary">{chip}</ListChip>)
                    : <ListChip tone="primary">{i.t('pushes:segment.cartsAll')}</ListChip>}
            </div>

            <div className="pu-segment__stats">
                <ListMetric value={size ? size.count.toLocaleString() : null} label={i.t('pushes:segment.cartsCount')} />
                <ListMetric
                    value={size ? size.reachable.toLocaleString() : null}
                    label={i.t('pushes:segment.reachable')}
                    hint={i.t('pushes:segment.reachableHint')}
                    tone={size && size.reachable > 0 ? 'success' : undefined}
                />
            </div>

            {size && size.count === 0 && <Alert type="error" showIcon message={i.t('pushes:segment.cartsEmpty')} />}
            {locked &&
                <Alert
                    type="warning"
                    showIcon
                    message={i.t('pushes:segment.planHint')}
                    action={<Button size="small" href="/billing">{i.t('pushes:segment.plans')}</Button>}
                />
            }
            <div className="pu-hint">{i.t('pushes:segment.cartsHint')}</div>
        </div>
    )
})

export const Push = observer(() => {

    const { id = 0 }: any = useParams<ParamTypes>()
    const location = useLocation()
    const [platform, setPlatform] = useState<PreviewPlatform>('ios')

    useEffect(() => {
        pushesController.loadTimezone()
        if (id > 0) pushesController.getOne(id)

        const params = new URLSearchParams(location.search)
        const userId = params.get('userId')
        if (userId) pushesController.preselectUserById(Number(userId))

        // Рассылка по сегменту приходит из списка клиентов; у сохранённого пуша адресаты уже заданы
        if (Number(id) === 0) pushesController.setSegmentFromUrl(params.get('segment'))
        // …или из списка корзин; контроллер общий, поэтому для любого другого пуша сегмент корзин сбрасываем
        pushesController.setCartsSegmentFromUrl(Number(id) === 0 ? params.get('carts') : null)
    }, [])

    const appName = useMemo(() => {
        try {
            return returnParseProjectElement('name') || 'SalesKit'
        } catch (e) {
            return 'SalesKit'
        }
    }, [])

    const image = useImageUrl()
    const push = pushesController.push_create
    const isInstant = pushesController.push_type === 'instant'
    const showDevices = pushesController.push_type === 'instant' || pushesController.push_type === 'delayed'
    const recipients = pushesController.selected_users.length
    const segment = pushesController.segment
    const cartsSegment = pushesController.cartsSegment
    const anySegment = !!segment || !!cartsSegment
    const segmentSize = pushesController.segmentSize
    // Адресаты сохранённого пуша при редактировании не меняются
    const fixedRecipients = Number(id) > 0 ? ((push as any).recipients_count ?? 0) : 0
    const segmentBlocked = anySegment && (storeLocal.get('pricing_plan') < PUSH_SEGMENT_PLAN || segmentSize?.count === 0)

    const audienceSummary = anySegment
        ? i.t(cartsSegment ? 'pushes:segment.cartsSummary' : 'pushes:segment.summary', { count: segmentSize ? segmentSize.count.toLocaleString() as any : '…' })
        : fixedRecipients > 0
            ? i.t('pushes:editor.recipientsSelected', { count: fixedRecipients })
            : recipients > 0 ? i.t('pushes:editor.recipientsSelected', { count: recipients }) : i.t('pushes:editor.toAll')

    return (
        <>
            <Titles
                title={id > 0 ? i.t('pushes:editor.editTitle') : i.t('pushes:push.title')}
                buttons={[{ title: i.t('def:actions.back'), href: `/pushes`, type: 'dashed' }]}
            />

            <form className="pu-editor" onSubmit={(e) => pushesController.create(e)}>
                <div className="pu-editor__main">
                    {showDevices &&
                        <Section
                            icon={<ClockCircleOutlined />}
                            title={i.t('pushes:push.form.postpone_to')}
                            background="linear-gradient(135deg, #7a4f9e, #a37bc4)"
                        >
                            <ErrorByPricingPlan
                                plan={1}
                                content={
                                    <Segmented
                                        block
                                        value={pushesController.push_type}
                                        onChange={(value) => pushesController.onChangeType(value as string)}
                                        disabled={!storeLocal.get('pricing_plan') || storeLocal.get('pricing_plan') < 1}
                                        options={[
                                            { value: 'instant', label: <span><SendOutlined /> {i.t('pushes:push.filter.instant')}</span> },
                                            { value: 'delayed', label: <span><ClockCircleOutlined /> {i.t('pushes:push.filter.delayed')}</span> },
                                        ]}
                                    />
                                }
                            />
                            {pushesController.push_type === 'delayed' &&
                                <PlainField label={i.t('pushes:sendTimeProject', { timezone: pushesController.projectTimezone })}>
                                    <DatePicker
                                        size="large"
                                        style={{ width: '100%' }}
                                        format="YYYY-MM-DD HH:00"
                                        showTime={{ format: 'HH' }}
                                        allowClear={false}
                                        value={push.postpone_to > 0 ? pushesController.wallClock(push.postpone_to) : null}
                                        disabledDate={(current) => !!current && pushesController.isPastDay(current)}
                                        onChange={(value) => {
                                            // Время проекта, а не браузера; рассылка идёт с точностью до часа
                                            if (value) push.postpone_to = pushesController.fromWallClock(value)
                                        }}
                                    />
                                </PlainField>
                            }
                        </Section>
                    }

                    <Section
                        icon={<EditOutlined />}
                        title={i.t('pushes:editor.content')}
                        background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                    >
                        <PlainField label={i.t('pushes:push.form.title')}>
                            <Input
                                id="push-title"
                                size="large"
                                value={push.title}
                                onChange={(e) => push.title = e.target.value}
                                maxLength={TITLE_MAX}
                                showCount
                                placeholder={i.t('pushes:editor.titlePlaceholder')}
                                required
                            />
                        </PlainField>
                        <PlainField label={i.t('pushes:push.form.description')}>
                            <Input.TextArea
                                id="push-text"
                                value={push.description}
                                onChange={(e) => push.description = e.target.value}
                                maxLength={TEXT_MAX}
                                showCount
                                autoSize={{ minRows: 3, maxRows: 6 }}
                                placeholder={i.t('pushes:editor.textPlaceholder')}
                            />
                        </PlainField>
                        <PlainField label={i.t('pushes:push.form.image')}>
                            <ImageField image={image} />
                        </PlainField>
                    </Section>

                    {(showDevices || pushesController.push_type !== 'birthday') &&
                        <Section
                            icon={<TeamOutlined />}
                            title={i.t('pushes:editor.audience')}
                            background="linear-gradient(135deg, #34526e, #567f9e)"
                        >
                            {showDevices &&
                                <PlainField label={i.t('pushes:push.form.devices')}>
                                    <Segmented
                                        block
                                        value={push.devices}
                                        onChange={(value) => push.devices = value as string}
                                        options={[
                                            { value: 'all', label: <span><MobileOutlined /> {i.t('pushes:push.form.devices_all')}</span> },
                                            { value: 'ios', label: <span><AppleFilled /> iOS</span> },
                                            { value: 'android', label: <span><AndroidFilled /> Android</span> },
                                        ]}
                                    />
                                </PlainField>
                            }
                            {pushesController.push_type !== 'birthday' && segment && <SegmentAudience />}
                            {pushesController.push_type !== 'birthday' && cartsSegment && <CartsAudience />}
                            {pushesController.push_type !== 'birthday' && !anySegment && fixedRecipients > 0 &&
                                <PlainField label={i.t('pushes:selectRecipients')}>
                                    <div className="pu-hint">{i.t('pushes:segment.fixedRecipients', { count: fixedRecipients })}</div>
                                </PlainField>
                            }
                            {pushesController.push_type !== 'birthday' && !anySegment && fixedRecipients === 0 &&
                                <PlainField label={i.t('pushes:selectRecipients')}>
                                    <SelectUsersModal
                                        selected={pushesController.selected_users}
                                        onSelect={(user: any) => pushesController.selectUser(user)}
                                        title={i.t('pushes:selectRecipients')}
                                    />
                                    <div className="pu-hint" style={{ marginTop: 6 }}>
                                        {recipients > 0
                                            ? i.t('pushes:editor.recipientsSelected', { count: recipients })
                                            : i.t('pushes:editor.recipientsHint')
                                        }
                                    </div>
                                </PlainField>
                            }
                        </Section>
                    }

                    {pushesController.push_type !== 'birthday' &&
                        <Section
                            icon={<GiftOutlined />}
                            title={i.t('pushes:bonus.title')}
                            background="linear-gradient(135deg, #b0603a, #d98f5c)"
                        >
                            <PushBonusFields value={push} />
                        </Section>
                    }

                    {pushesController.push_type !== 'birthday' &&
                        <Section
                            icon={<LinkOutlined />}
                            title={i.t('pushes:editor.action')}
                            background="linear-gradient(135deg, #2f6f5e, #4c9b85)"
                        >
                            <div className="pu-hint">{i.t('pushes:editor.actionHint')}</div>
                            <SelectEntity
                                type={__select.select.type}
                                element={__select.select.value}
                                item_element={__select.select.element}
                            />
                        </Section>
                    }

                    <div className="pu-editor__actions">
                        <span className="pu-hint">
                            {audienceSummary}
                            {showDevices && <> · {push.devices === 'ios' ? 'iOS' : push.devices === 'android' ? 'Android' : i.t('pushes:push.form.devices_all')}</>}
                        </span>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Button href="/pushes">{i.t('def:actions.back')}</Button>
                            <Button type="primary" htmlType="submit" icon={isInstant ? <SendOutlined /> : undefined} size="large" disabled={segmentBlocked}>
                                {isInstant ? i.t('def:actions.send') : i.t('def:actions.save')}
                            </Button>
                        </div>
                    </div>
                </div>

                <aside className="pu-editor__aside">
                    <div className="pu-preview">
                        <div className="pu-preview__head">
                            <h3 className="pu-preview__title">{i.t('pushes:editor.preview')}</h3>
                            <Segmented
                                size="small"
                                value={platform}
                                onChange={(value) => setPlatform(value as PreviewPlatform)}
                                options={[
                                    { value: 'ios', label: <AppleFilled />, title: 'iOS' },
                                    { value: 'android', label: <AndroidFilled />, title: 'Android' },
                                ]}
                            />
                        </div>
                        <NotificationPreview platform={platform} appName={appName} image={image} />
                        <div className="pu-preview__hint">{i.t('pushes:editor.previewHint')}</div>
                    </div>
                </aside>
            </form>
        </>
    )
})
